package org.example.project.viewmodel

import androidx.compose.runtime.*
import com.battleships.quiz.Question
import com.battleships.quiz.QuizSession
import kotlinx.coroutines.Job
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import org.example.project.firebase.FirestoreManager
import org.example.project.firebase.GameManager
import org.example.project.firebase.MatchmakingManager
import org.example.project.game.AttackEngine
import org.example.project.model.*
import org.example.project.util.currentTimeMillis
import org.example.project.utils.AudioManager

class GameViewModel {

    var playerName by mutableStateOf("")
        private set

    var myUid by mutableStateOf("")
        private set


    var gameId by mutableStateOf<String?>(null)
        private set

    var multiplayerGame by mutableStateOf<MultiplayerGame?>(null)
        private set

    var matchmakingError by mutableStateOf<String?>(null)
        private set

    var matchMakingText by mutableStateOf(
        "Find an opponent\nand start a battle"
    )

    var isMatchmaking by mutableStateOf(false)
        private set

    var currentQuestion by mutableStateOf<Question?>(null)
        private set

    var playersInQueue by mutableStateOf(0)
        private set

    var showQuestionDialog by mutableStateOf(false)
        private set

    var gameState by mutableStateOf(GameState.HOME)
        private set

    var playerBoard by mutableStateOf(Board())
        private set

    var enemyBoard by mutableStateOf(Board())
        private set

    var opponentName by mutableStateOf("Enemy Captain")
        private set

    var shipsPlaced by mutableStateOf(false)
        private set

    var fleetDeployed by mutableStateOf(false)
        private set

    var volume by mutableFloatStateOf(1f)
        private set

    var selectedShip by mutableStateOf<ShipType?>(ShipType.CARRIER)
        private set

    var orientation by mutableStateOf(Orientation.HORIZONTAL)
        private set

    var placedShips = mutableStateListOf<ShipType>()
        private set

    var deployedShips = mutableStateListOf<PlacedShip>()
        private set

    var pendingRow by mutableStateOf(-1)
        private set

    var pendingCol by mutableStateOf(-1)
        private set

    var selectedAttackType by mutableStateOf(AttackType.TORPEDO)
        private set

    var lineStrikeHorizontal by mutableStateOf(true)
        private set

    var battlePhase by mutableStateOf(BattlePhase.WAITING_FOR_TARGET)
        private set

    var explosionRow by mutableStateOf(-1)
        private set

    var explosionCol by mutableStateOf(-1)
        private set

    var showExplosion by mutableStateOf(false)
        private set

    var matchCap by mutableStateOf(false)
        private set

    var showPenaltyDialog by mutableStateOf(false)
        private set

    var statusMessage by mutableStateOf("Select an attack type, then target the enemy grid.")
        private set

    var localScore by mutableIntStateOf(0)
        private set

    var localPenalties by mutableIntStateOf(0)
        private set

    var localCorrect by mutableIntStateOf(0)
        private set

    var showAttackConfirmation by mutableStateOf(false)
        private set

    var showForfeitDialog by mutableStateOf(false)

    var pendingAttackRow by mutableStateOf(-1)
        private set

    var pendingAttackCol by mutableStateOf(-1)
        private set

    var turnTimeRemaining by mutableIntStateOf(60)
        private set

    var questionTimeRemaining by mutableIntStateOf(30)
        private set

    var soundEnabled by mutableStateOf(true)
        private set

    var animationsEnabled by mutableStateOf(true)
        private set

    var showCoordinates by mutableStateOf(false)
        private set

    private var timerJob: Job? = null

    var timerMode by mutableStateOf(TimerMode.NONE)

    private val scope = MainScope()
    private val quiz = QuizSession(myUid)
    private var gameObserverJob: Job? = null
    private var statsSaved = false

    private var lastTurnUid = ""
    private var lastTurnStartTime = 0.0

    val isMyTurn: Boolean
        get() {
            val game = multiplayerGame ?: return false
            return game.isMyTurn(myUid)
        }

    val myCharges: AttackCharges
        get() = multiplayerGame?.myCharges(myUid) ?: AttackCharges()


    fun resetToHome() {
        gameObserverJob?.cancel()
        gameObserverJob = null
        gameId = null
        multiplayerGame = null
        statsSaved = false
        resetPlacement()
        gameState = GameState.HOME
        statusMessage = "Select an attack type, then target the enemy grid."
        timerJob?.cancel()
        timerJob = null
    }

    fun startNewMatch(user: User) {

        gameObserverJob?.cancel()
        gameObserverJob = null

        timerJob?.cancel()
        timerJob = null

        gameId = null
        multiplayerGame = null
        statsSaved = false

        pendingRow = -1
        pendingCol = -1
        pendingAttackRow = -1
        pendingAttackCol = -1

        selectedAttackType = AttackType.TORPEDO
        battlePhase = BattlePhase.WAITING_FOR_TARGET

        showQuestionDialog = false
        showPenaltyDialog = false
        showAttackConfirmation = false
        showExplosion = false

        localScore = 0
        localPenalties = 0
        localCorrect = 0
        opponentName = "Enemy Captain"
        lastTurnUid = ""
        lastTurnStartTime = 0.0

        statusMessage = "Searching for opponent..."

        resetPlacement()

        startMatchmaking(user)
    }

    fun startMatchmaking(user: User) {
        playerName = user.username
        myUid = user.uid
        matchmakingError = null
        isMatchmaking = true
        gameState = GameState.MATCHMAKING


        if(user.gamesPlayed <=12){
        scope.launch {
            val id = MatchmakingManager.findMatch(user)
            isMatchmaking = false

            if (id != null) {
                gameId = id
                startTimerEngine()
                startObservingGame(id)
                gameState = GameState.SHIP_PLACEMENT

            } else {
                matchmakingError = "No opponent found. Try again."
                gameState = GameState.HOME
            }
            matchMakingText = "Find an opponent\nand start a battle"


          }
        }
        else {
            matchmakingError = "Max Limit Reached! You will be remembered!."
            gameState = GameState.HOME
            matchCap = true
            matchMakingText = "\"Really Sorry!\nyou have reached your max limit\""
        }
    }

    fun cancelMatchmaking() {
        scope.launch {
            MatchmakingManager.leaveQueue()
            isMatchmaking = false
            gameState = GameState.HOME
        }
    }



    private fun startObservingGame(id: String) {
        gameObserverJob?.cancel()
        gameObserverJob = scope.launch {
            GameManager.observeGame(id)
                .catch { e ->
                    println("Game observer error: ${e.message}")
                }
                .collect { game ->

                    if (game == null) return@collect

                    val turnChanged =
                        game.currentTurnUid != lastTurnUid ||
                                game.turnStartTime != lastTurnStartTime

                    onGameUpdated(game)

                    if (turnChanged) {

                        lastTurnUid = game.currentTurnUid
                        lastTurnStartTime = game.turnStartTime
                        if (game.isMyTurn(myUid)) {

                            timerMode = TimerMode.TURN

                            scope.launch {
                                GameManager.updatePlayerTimerMode(
                                    gameId = id,
                                    uid = myUid,
                                    mode = TimerMode.TURN
                                )
                            }

                        } else {

                            timerMode = TimerMode.NONE
                        }

                    }
                }
        }
    }

    fun refreshPlayersInQueue() {
        scope.launch {
            playersInQueue = FirestoreManager.getPlayersInQueue()
        }
    }

    private fun onGameUpdated(game: MultiplayerGame) {
        multiplayerGame = game
        opponentName = game.opponentName(myUid)

        localScore = game.myScore(myUid)
        localPenalties = game.myPenalties(myUid)
        localCorrect = game.myCorrect(myUid)

        syncBoardsFromGame(game)
        statusMessage = game.lastEvent.ifEmpty { statusMessage }

        when (game.status) {
            "SHIP_PLACEMENT" -> {
                if (gameState != GameState.SHIP_PLACEMENT) {
                    gameState = GameState.SHIP_PLACEMENT
                }
            }
            "COUNTDOWN" -> {
                if (gameState != GameState.COUNTDOWN && gameState != GameState.BATTLE) {
                    gameState = GameState.COUNTDOWN
                }
            }
            "BATTLE" -> {
                if (gameState == GameState.COUNTDOWN || gameState == GameState.SHIP_PLACEMENT) {
                    gameState = GameState.BATTLE
                }
                updateTurnStatus(game)
            }
            "FINISHED" -> {
                if (gameState != GameState.RESULT) {
                    gameState = GameState.RESULT
                    if (!statsSaved) {
                        statsSaved = true
                        scope.launch {
                            GameManager.finalizeGameStats(game, myUid)
                            timerJob?.cancel()
                        }
                    }
                }
            }
        }
    }
    private fun startTimerEngine() {

        timerJob?.cancel()

        timerJob = scope.launch {

            while (currentCoroutineContext().isActive)  {

                when (timerMode) {

                    TimerMode.TURN -> {

                        val game = multiplayerGame ?: break

                        val elapsed =
                            ((currentTimeMillis() -
                                    game.turnStartTime) / 1000).toInt()

                        turnTimeRemaining =
                            (60 - elapsed).coerceAtLeast(0)

                        if (turnTimeRemaining == 0) {
                            timerMode = TimerMode.NONE
                            if (game.isMyTurn(myUid)) {

                                GameManager.forfeit(
                                    game.gameId,
                                    myUid
                                )
                            }
                        }

                        delay(1000)
                    }

                    TimerMode.QUESTION -> {

                        val game = multiplayerGame ?: break
                        while (
                            timerMode == TimerMode.QUESTION &&
                            questionTimeRemaining > 0
                        ) {
                            delay(1000)
                            questionTimeRemaining--
                        }

                        if (
                            timerMode == TimerMode.QUESTION &&
                            questionTimeRemaining == 0
                        ) {
                            timerMode = TimerMode.NONE
                            GameManager.updatePlayerTimerMode(
                                game.gameId,
                                myUid,
                                TimerMode.NONE
                            )
                            showPenaltyDialog = true
                            battlePhase = BattlePhase.PENALTY
                            statusMessage = "Wrong answer — systems locked!"
                            showQuestionDialog = false
                            AudioManager.playSystemDownSound()
                            if (game.isMyTurn(myUid)) {

                                GameManager.recordWrongAnswer(
                                    game.gameId,
                                    myUid
                                )
                            }
                        }
                    }
                    TimerMode.NONE -> {

                        delay(100)
                    }
                }
            }
        }
    }
    private fun updateTurnStatus(game: MultiplayerGame) {
        statusMessage = when {
            game.lastEvent.isNotEmpty() -> game.lastEvent
            game.isMyTurn(myUid) -> "Your turn — pick an attack and target the enemy!"
            else -> "Waiting for ${game.opponentName(myUid)}..."
        }
    }

    private fun syncBoardsFromGame(game: MultiplayerGame) {
        val myShips = deployedShips.ifEmpty {
            game.myShips(myUid).map { it.toPlacedShip() }
        }
        if (deployedShips.isEmpty() && myShips.isNotEmpty()) {
            deployedShips.addAll(myShips)
            placedShips.addAll(myShips.map { it.type })
        }

        playerBoard = AttackEngine.mergeMyBoardWithAttacks(
            myShips,
            game.attacksOnMe(myUid)
        )

        val enemyCells = AttackEngine.attackGridToBoardCells(
            game.attacksOnOpponent(myUid)
        )
        val newEnemyBoard = Board()
        for (row in 0 until 10) {
            for (col in 0 until 10) {
                newEnemyBoard.cells[row][col] = enemyCells[row][col]
            }
        }
        enemyBoard = newEnemyBoard
    }

    fun onCountdownFinished() {
        scope.launch {
            gameId?.let { GameManager.startBattle(it) }
        }
    }

    fun forfeit() {

        val id = gameId ?: return

        scope.launch {

            GameManager.forfeit(
                gameId = id,
                loserUid = myUid
            )

            showForfeitDialog = false
        }
    }

    fun formattedTime(): String {

        val time =
            if (timerMode == TimerMode.QUESTION)
                questionTimeRemaining
            else
                turnTimeRemaining

        val minutes = time / 60
        val seconds = time % 60

        val minString =
            if (minutes < 10) "0$minutes"
            else minutes.toString()

        val secString =
            if (seconds < 10) "0$seconds"
            else seconds.toString()

        return "$minString:$secString"
    }

    fun deployFleet() {
        val id = gameId ?: return
        if (fleetDeployed) return
        fleetDeployed = true
        val ships = deployedShips.map { SerializedShip.fromPlacedShip(it) }

        scope.launch {
            GameManager.submitFleet(id, myUid, ships)
            statusMessage = "Fleet deployed — waiting for opponent..."
        }
    }

    fun selectAttackType(type: AttackType) {
        if (!myCharges.canUse(type)) return
        selectedAttackType = type
        battlePhase = BattlePhase.WAITING_FOR_TARGET
        statusMessage = "${type.displayName} armed — select a target"
    }

    fun toggleLineStrikeOrientation() {
        lineStrikeHorizontal = !lineStrikeHorizontal
        statusMessage = if (lineStrikeHorizontal) {
            "Line Strike: horizontal row"
        } else {
            "Line Strike: vertical column"
        }
    }

    fun selectTarget(row: Int, col: Int) {
        if (!isMyTurn) {
            statusMessage = "Not your turn!"
            return
        }
        if (battlePhase != BattlePhase.WAITING_FOR_TARGET) return

        val attackGrid = multiplayerGame?.attacksOnOpponent(myUid) ?: emptyList()
        if (attackGrid.size == 100) {
            val state = AttackCellState.valueOf(attackGrid[row * 10 + col])
            if (state == AttackCellState.HIT || state == AttackCellState.MISS) {
                if (selectedAttackType != AttackType.SONAR) return
            }
        }

        pendingRow = row
        pendingCol = col
        battlePhase = BattlePhase.QUESTION_ACTIVE
        loadQuestion()
    }

    fun loadQuestion() {
        currentQuestion = quiz.randomQuestion()
        timerMode = TimerMode.QUESTION
        scope.launch {
            GameManager.updatePlayerTimerMode(
                gameId = gameId!!,
                uid = myUid,
                mode = TimerMode.QUESTION
            )
        }
        questionTimeRemaining = 30
        showQuestionDialog = true
    }

    fun setSoundEnabled(value: Boolean) {
        soundEnabled = value
        if (soundEnabled)
        AudioManager.unMute()
        else
        AudioManager.mute()
    }

    fun setVolume(value: Float) {
        volume = value
        AudioManager.setVolume(value)
    }

    fun setAnimationsEnabled(value: Boolean) {
        animationsEnabled = value
    }

    fun setShowCoordinates(value: Boolean) {
        showCoordinates = value
    }

    fun submitAnswer(answer: String): Boolean {
        val correct = currentQuestion?.let { quiz.checkAnswer(it, answer) } ?: false
        showQuestionDialog = false
        currentQuestion = null
        timerMode = TimerMode.TURN
        scope.launch {
            GameManager.updatePlayerTimerMode(
                gameId!!,
                myUid,
                TimerMode.NONE
            )
        }
        return correct
    }

    fun onQuestionAnswered(correct: Boolean) {
        timerMode= TimerMode.NONE
        scope.launch {
            GameManager.updatePlayerTimerMode(
                gameId!!,
                myUid,
                TimerMode.NONE
            )
        }
        if (!correct) {
            showPenaltyDialog = true
            battlePhase = BattlePhase.PENALTY
            statusMessage = "Wrong answer — systems locked!"
            scope.launch {
                gameId?.let { GameManager.recordWrongAnswer(it, myUid) }
            }
            AudioManager.playSystemDownSound()
            return
        }

        battlePhase = BattlePhase.ATTACK_RESOLVING
        scope.launch {
            val id = gameId ?: return@launch
            val result = GameManager.executeAttack(
                gameId = id,
                attackerUid = myUid,
                attackType = selectedAttackType,
                row = pendingRow,
                col = pendingCol,
                lineHorizontal = lineStrikeHorizontal
            )
            println("Attack Type = $selectedAttackType")

            playAttackSound(selectedAttackType)

            if (selectedAttackType != AttackType.SONAR) {
                triggerExplosion(pendingRow, pendingCol)
            }

            result?.let {
                if (it.winnerUid.isNotEmpty()) {
                    statusMessage = if (it.winnerUid == myUid) {
                        "Victory! You destroyed the enemy fleet!"
                    } else {
                        "Defeat! Your fleet was destroyed."
                    }
                }
            }

            selectedAttackType = AttackType.TORPEDO
            battlePhase = BattlePhase.WAITING_FOR_TARGET
        }
    }
    private fun playAttackSound(type: AttackType) {

        when(type){

            AttackType.TORPEDO ->
                AudioManager.playTorpedo()

            AttackType.CROSS_FIRE ->
                AudioManager.playCrossFire()

            AttackType.CLUSTER_BOMB ->
                AudioManager.playClusterBomb()

            AttackType.LINE_STRIKE ->
                AudioManager.playLineStrike()

            AttackType.SONAR ->
                AudioManager.playSonarPing()
        }
    }

    fun endPenalty() {
        showPenaltyDialog = false
        battlePhase = BattlePhase.WAITING_FOR_TARGET
        timerMode =
            if (isMyTurn)
                TimerMode.TURN
            else
                TimerMode.NONE

        scope.launch {
            GameManager.updatePlayerTimerMode(
                gameId!!,
                myUid,
                TimerMode.NONE
            )
        }
    }

    fun navigateTo(state: GameState) {
        gameState = state
    }

    fun changeOrientation() {
        orientation = if (orientation == Orientation.HORIZONTAL) {
            Orientation.VERTICAL
        } else {
            Orientation.HORIZONTAL
        }
    }

    fun selectShip(ship: ShipType) {
        selectedShip = ship
    }

    private fun resetPlacement() {
        playerBoard = Board()
        enemyBoard = Board()
        shipsPlaced = false
        selectedShip = ShipType.CARRIER
        orientation = Orientation.HORIZONTAL
        placedShips.clear()
        deployedShips.clear()
        fleetDeployed = false
    }

    private fun canPlaceShip(row: Int, col: Int, size: Int): Boolean {
        for (i in 0 until size) {
            val r = if (orientation == Orientation.HORIZONTAL) row else row + i
            val c = if (orientation == Orientation.HORIZONTAL) col + i else col
            if (r >= 10 || c >= 10) return false
            if (playerBoard.cells[r][c] != CellState.EMPTY) return false
        }
        return true
    }

    fun placeShip(row: Int, col: Int) {
        val ship = selectedShip ?: return
        if (placedShips.contains(ship)) return
        if (!canPlaceShip(row, col, ship.size)) return

        for (i in 0 until ship.size) {
            val r = if (orientation == Orientation.HORIZONTAL) row else row + i
            val c = if (orientation == Orientation.HORIZONTAL) col + i else col
            playerBoard.cells[r][c] = CellState.SHIP
        }

        deployedShips.add(
            PlacedShip(
                type = ship,
                row = row,
                col = col,
                horizontal = orientation == Orientation.HORIZONTAL
            )
        )

        placedShips.add(ship)
        selectedShip = ShipType.entries.firstOrNull { !placedShips.contains(it) }

        if (placedShips.size == ShipType.entries.size) {
            shipsPlaced = true
        }
    }



    fun triggerExplosion(row: Int, col: Int) {
        explosionRow = row
        explosionCol = col
        showExplosion = true
    }

    fun hideExplosion() {
        showExplosion = false
    }

    fun removeShip(ship: PlacedShip) {
        for (i in 0 until ship.type.size) {
            val r = if (ship.horizontal) ship.row else ship.row + i
            val c = if (ship.horizontal) ship.col + i else ship.col
            playerBoard.cells[r][c] = CellState.EMPTY
        }
        deployedShips.remove(ship)
        placedShips.remove(ship.type)
        shipsPlaced = false
        if (selectedShip == null) selectedShip = ship.type
    }

    fun shipAt(row: Int, col: Int): PlacedShip? =
        deployedShips.firstOrNull { ship ->
            (0 until ship.type.size).any { i ->
                val r = if (ship.horizontal) ship.row else ship.row + i
                val c = if (ship.horizontal) ship.col + i else ship.col
                r == row && c == col
            }
        }

    fun didIWin(): Boolean =
        multiplayerGame?.winnerUid == myUid

    fun remainingEnemyCells(): Int {
        val game = multiplayerGame ?: return 0
        return AttackEngine.remainingShipCells(
            game.attacksOnOpponent(myUid),
            game.opponentShips(myUid)
        )
    }



    fun requestAttack(row: Int, col: Int) {

        if (!myCharges.canUse(selectedAttackType)) {
            statusMessage = "No ${selectedAttackType.displayName} charges remaining."
            return
        }

        pendingAttackRow = row
        pendingAttackCol = col
        showAttackConfirmation = true
    }

    fun confirmAttack() {

        showAttackConfirmation = false

        selectTarget(
            pendingAttackRow,
            pendingAttackCol
        )

        pendingAttackRow = -1
        pendingAttackCol = -1
    }

    fun cancelAttack() {
        showAttackConfirmation = false
        pendingAttackRow = -1
        pendingAttackCol = -1
    }

}
